import { ReactNode } from 'react';

import {
  AssistantProvider,
  DRAWER_IDS,
  DRAWER_LABELS,
  DrawerId,
  useContextBarContext,
} from 'src/context';
import { ContextBar } from 'src/features/context-bar/ContextBar';
import { ContextBarAvatar } from 'src/features/context-bar/ContextBarAvatar';
import {
  ContextBarNavTabs,
  NavTab,
} from 'src/features/context-bar/ContextBarNavTabs';
import { NoteModalProvider } from 'src/features/notes/NoteModal/noteModalContext';
import { ResizableGroup } from 'src/features/resizableWrapper/ResizableGroup';
import {
  getRightPanelSizeProps,
  ResizablePanel,
} from 'src/features/resizableWrapper/ResizablePanel';
import { ResizeHandle } from 'src/features/resizableWrapper/ResizeHandle';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { useAppSelector } from 'src/hooks/useStore';
import { useWindowSize } from 'src/hooks/useWindowResize';
import { selectCurrentUser } from 'src/store/storeUtils';

export const ContextBarLayout = ({ children }: { children: ReactNode }) => {
  const currentUser = useAppSelector(selectCurrentUser);
  const hasAssistantTab = !currentUser?.isClientAccessLimited;

  const { width: viewPortWidth } = useWindowSize();
  const isMobileScreen = useMediaQuery('xs', 'max');
  const { open, getActiveDrawer, setLastOpenDrawer, target } =
    useContextBarContext();
  const { entityType, id: entityId } = target ?? {};

  const navTabs: Array<NavTab<DrawerId>> = [
    {
      key: DRAWER_IDS.DETAILS,
      label: DRAWER_LABELS[DRAWER_IDS.DETAILS],
      icon: 'Profile',
    },
    {
      key: DRAWER_IDS.NOTES,
      label: DRAWER_LABELS[DRAWER_IDS.NOTES],
      icon: 'Templates',
    },
    ...(hasAssistantTab
      ? ([
          {
            key: DRAWER_IDS.INTERNAL_CHAT,
            label: DRAWER_LABELS[DRAWER_IDS.INTERNAL_CHAT],
            icon: 'Comment',
          },
        ] as Array<NavTab<DrawerId>>)
      : []),
  ];

  const activeTabKey = getActiveDrawer(hasAssistantTab);

  return (
    <AssistantProvider>
      <ResizableGroup widthPersistKey="context-bar">
        <ResizablePanel id="app-content" order={1} className="flex flex-col">
          {children}
        </ResizablePanel>
        {open && (
          <>
            <ResizeHandle />
            <ResizablePanel
              id="context-bar-content"
              order={2}
              {...getRightPanelSizeProps(viewPortWidth, isMobileScreen)}
            >
              <NoteModalProvider>
                <ContextBar />
              </NoteModalProvider>
            </ResizablePanel>
          </>
        )}
        <div className="w-11 h-full flex flex-col items-center shrink-0 border-0 border-l border-solid border-non-hover-border bg-gray-100">
          {entityType && entityId && (
            <ContextBarAvatar
              className="my-4"
              entityType={entityType}
              entityId={entityId}
            />
          )}
          <ContextBarNavTabs<DrawerId>
            className="py-1.5"
            tabs={navTabs}
            activeTabKey={activeTabKey}
            onTabChange={(drawerId) => setLastOpenDrawer(drawerId)}
          />
        </div>
      </ResizableGroup>
    </AssistantProvider>
  );
};
